import { useCallback, useEffect, useState } from "react";
import { shallowEqual } from "react-redux";
import { styled } from "styled-components";

import { ErrorMessage, StyledModalText, UIButton } from "common/components";
import { UIInput } from "common/components/form/UIInput";
import { transformKnowledgePackage, useDispatch, useMemoisedSelector, useSelector } from "common/utilities/react-redux";
import { usePolling } from "common/utilities/usePolling";
import { useTranslate } from "common/utilities/useTranslate";
import { applyKnowledgeDataSearchCriteria } from "domains/knowledgeData/actions";
import { getKnowledgeDataSearchCriteria } from "domains/knowledgeData/selectors";
import { applyLatestSearchCriteria } from "domains/knowledgeFeed/actions";
import { createKnowledgeFeedForTopicSelector, getLatestSearchCriteria } from "domains/knowledgeFeed/selectors";
import { ModalHeader } from "domains/modal/components/ModalHeader";
import { getModalParams } from "domains/modal/selectors";
import type { ModalParams } from "domains/modal/types";
import { addKnowledge } from "domains/pinAddReject/actions";
import { KnowledgePreview } from "domains/pinAddReject/components/KnowledgePreview";
import {
  getErrorForAddedKnowledge,
  getIsAddedKnowledgePending,
  getNewKnowledgeId,
} from "domains/pinAddReject/selectors";
import { applyPublicationSearchCriteria } from "domains/publication/actions";
import { getPublicationSearchCriteria } from "domains/publication/selectors";
import { applyVideoSearchCriteria } from "domains/videos/actions";
import { getVideoSearchCriteria } from "domains/videos/selectors";
import { fetchKnowledgeById } from "entities/knowledge/actions";
import { createKnowledgesSelector, getKnowledgeById } from "entities/knowledge/selectors";

const StyledUIInputContainer = styled.div`
  display: flex;
  flex-direction: column;
  row-gap: 10px;
  margin-bottom: 30px;
`;

export const AddKnowledgeModal = () => {
  const dispatch = useDispatch();
  const addKnowledgeError = useSelector(getErrorForAddedKnowledge);
  const isPending = useSelector(getIsAddedKnowledgePending);
  const knowledgedataSearchCriteria = useSelector(getKnowledgeDataSearchCriteria, shallowEqual);
  const latestSearchCriteria = useSelector(getLatestSearchCriteria, shallowEqual);
  const { topicId } = useSelector(getModalParams) as ModalParams<"AddKnowledge">;
  const newKnowledgeId = useSelector(getNewKnowledgeId);
  const publicationSearchCriteria = useSelector(getPublicationSearchCriteria, shallowEqual);
  const videoSearchCriteria = useSelector(getVideoSearchCriteria, shallowEqual);
  const [areSectionsRefetched, setAreSectionsRefetched] = useState<boolean>(false);
  const [articleUrl, setArticleUrl] = useState<string>("");
  const [articleUrlError, setArticleUrlError] = useState<string | null>(null);
  const [isArticleUrlSaved, setIsArticleUrlSaved] = useState<boolean>(false);
  const translate = useTranslate();

  const knowledgeFeed = useMemoisedSelector(
    createKnowledgeFeedForTopicSelector(topicId),
    createKnowledgesSelector,
    transformKnowledgePackage,
  );
  const newKnowledge = useSelector(getKnowledgeById(newKnowledgeId), shallowEqual);

  const isError = !!addKnowledgeError || !!articleUrlError || newKnowledge?.status === "Error";

  const fetchNewKnowledge = useCallback(() => {
    if (newKnowledgeId) dispatch(fetchKnowledgeById({ id: newKnowledgeId, forceRefresh: true }));
  }, [dispatch, newKnowledgeId]);

  const refetchTopicSections = () => {
    if (newKnowledge?.status !== "Done" || areSectionsRefetched) return;

    dispatch(
      applyPublicationSearchCriteria(
        { limit: publicationSearchCriteria.limit + publicationSearchCriteria.offset, offset: 0 },
        { forceRefresh: true },
      ),
    );
    dispatch(
      applyVideoSearchCriteria(
        { limit: videoSearchCriteria.limit + videoSearchCriteria.offset, offset: 0 },
        { forceRefresh: true },
      ),
    );
    dispatch(
      applyKnowledgeDataSearchCriteria(
        { limit: knowledgedataSearchCriteria.limit + knowledgedataSearchCriteria.offset, offset: 0 },
        { forceRefresh: true },
      ),
    );

    dispatch(applyLatestSearchCriteria({ ...latestSearchCriteria, limit: knowledgeFeed.length, offset: 0, topicId }));

    setAreSectionsRefetched(true);
  };

  const showKnowledgePreview = () => {
    setIsArticleUrlSaved(true);
  };

  const { startPolling, stopPolling, isPolling, isTimeout } = usePolling({
    pollingCallback: fetchNewKnowledge,
    interval: 1000,
    onPollingStop: refetchTopicSections,
  });

  const submitArticleUrlForScraping = () => {
    if (isArticleUrlValid())
      dispatch(
        addKnowledge({
          link: articleUrl,
          topicIds: [topicId],
          onSuccess: showKnowledgePreview,
          onError: stopPolling,
        }),
      );
  };

  const isArticleUrlValid = () => {
    if (articleUrl.length === 0) {
      setArticleUrlError(translate("app.add_knowledge.error.enter_valid_url"));
      return false;
    }

    setArticleUrlError(null);
    return true;
  };

  const handleArticleUrlChange = (value: string) => {
    setArticleUrl(value);
    setIsArticleUrlSaved(false);
    stopPolling();
  };

  const resetArticleUrl = () => {
    setArticleUrl("");
    setIsArticleUrlSaved(false);
    stopPolling();
  };

  const doesKnowledgeNeedProcessing =
    !newKnowledge || newKnowledge.status === "Not Started" || newKnowledge.status === "Processing";

  const isProcessingFinished =
    newKnowledge?.status === "Done" ||
    newKnowledge?.status === "Error" ||
    newKnowledge?.status === null ||
    !!newKnowledge?.requestError;

  const getErrorMessage = () => {
    if (articleUrlError) return articleUrlError;

    if (newKnowledge?.status === "Error") return translate("app.add_knowledge.error.url_scraping_failed");

    if (addKnowledgeError) {
      switch (addKnowledgeError.status) {
        case 409:
          return translate("app.add_knowledge.error.url_rejected_previously");
        case 422:
          return translate("app.add_knowledge.error.url_malformed");
      }
    }

    return translate("app.add_knowledge.error.error_while_saving");
  };

  useEffect(() => {
    if (!isArticleUrlSaved || !newKnowledgeId || !doesKnowledgeNeedProcessing || isPolling) return;

    startPolling();
  }, [isArticleUrlSaved, doesKnowledgeNeedProcessing, isPolling, newKnowledgeId, startPolling]);

  useEffect(() => {
    if (doesKnowledgeNeedProcessing && !newKnowledge?.requestError) return;

    if (!isProcessingFinished) return;

    stopPolling();
  }, [doesKnowledgeNeedProcessing, isProcessingFinished, newKnowledge?.requestError, stopPolling]);

  return (
    <>
      <ModalHeader title={translate("app.add_knowledge.title")} />
      <StyledModalText>{translate("app.add_knowledge.description")}</StyledModalText>
      <StyledUIInputContainer>
        <UIInput
          name="articleUrlInput"
          onChange={handleArticleUrlChange}
          placeholder={translate("app.add_knowledge.url")}
          value={articleUrl}
          clear={resetArticleUrl}
        />
      </StyledUIInputContainer>
      <ErrorMessage isActive={isError}>{getErrorMessage()}</ErrorMessage>
      {!isArticleUrlSaved ? (
        <UIButton fullwidth onClick={submitArticleUrlForScraping} isLoading={isPending}>
          {translate("app.add_knowledge.save")}
        </UIButton>
      ) : (
        <>
          {isTimeout && !isProcessingFinished && (
            <>
              <StyledModalText>
                {translate("app.add_knowledge.processing.long_process_exit_and_return_later")}
              </StyledModalText>
              <StyledModalText>{translate("app.add_knowledge.processing.stay_for_realtime_updates")}</StyledModalText>
            </>
          )}
          <KnowledgePreview knowledge={newKnowledge} />
        </>
      )}
    </>
  );
};
